import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Download } from "lucide-react";
import { api, API } from "@/lib/api";

export default function PaymentResult({ cancelled }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isDownload = params.get("download") === "1";
  const isOpera = params.get("opera") === "1";
  const isShop = params.get("shop") === "1";
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      } catch {
        // silenzioso: il polling ritenta automaticamente
      }
      if (attempts < 8) setTimeout(poll, 2000);
      else setState("timeout");
    };
    poll();
  }, [cancelled, sessionId]);

  const paidContent = () => {
    if (isShop) {
      return {
        Icon: CheckCircle2, color: "#E4BD44",
        title: "Acquisto confermato!",
        text: "Grazie! Il tuo ordine Wildersquad è registrato: ti contatteremo entro 24 ore via email per consegna, accesso o organizzazione dell'esperienza.",
      };
    }
    if (isOpera) {
      return {
        Icon: CheckCircle2, color: "#E4BD44",
        title: "Opera acquistata!",
        text: "Complimenti, l'originale è tuo. Ti contatteremo entro 24 ore per organizzare consegna o spedizione assicurata e il certificato di autenticità.",
      };
    }
    if (isDownload) {
      return {
        Icon: CheckCircle2, color: "#E4BD44",
        title: "Acquisto confermato!",
        text: "La tua copia digitale è pronta. Il file include la licenza d'uso personale: è vietato il riutilizzo senza royalty concordate con Wildersquad.",
      };
    }
    return {
      Icon: CheckCircle2, color: "#E4BD44",
      title: "Caparra confermata!",
      text: "Sei ufficialmente in lista d'attesa per Assaggi d'Arte. Ti scriveremo appena la quota di 30 partecipanti sarà raggiunta per rivelarti la location segreta.",
    };
  };

  const content = {
    checking: {
      Icon: Loader2, spin: true, color: "#9a9484",
      title: "Verifica del pagamento in corso…",
      text: "Un attimo, stiamo confermando il tuo pagamento con Stripe.",
    },
    paid: paidContent(),
    failed: {
      Icon: XCircle, color: "#E4BD44",
      title: "Pagamento non riuscito",
      text: "La transazione non è andata a buon fine. Riprova dalla pagina Assaggi d'Arte.",
    },
    timeout: {
      Icon: Loader2, color: "#9a9484",
      title: "Stiamo ancora verificando",
      text: "La conferma sta impiegando più del previsto. Controlla la tua email: riceverai conferma appena il pagamento sarà registrato.",
    },
    cancelled: {
      Icon: XCircle, color: "#9a9484",
      title: "Pagamento annullato",
      text: "Nessun addebito effettuato. Il tuo posto in lista resta libero: puoi riprovare quando vuoi.",
    },
  }[state];

  return (
    <div className="max-w-xl mx-auto px-5 py-24 text-center fade-up" data-testid={`payment-${state}`}>
      <content.Icon size={48} className={`mx-auto mb-6 ${content.spin ? "animate-spin" : ""}`} style={{ color: content.color }} />
      <h1 className="font-display text-4xl font-black mb-4">{content.title}</h1>
      <p className="text-sm text-[#C9C4B8] leading-relaxed mb-10">{content.text}</p>
      {state === "paid" && isDownload && (
        <a href={`${API}/downloads/file/${sessionId}`} className="btn-primary mb-6 inline-flex" data-testid="download-file-button">
          <Download size={15} /> Scarica il tuo file
        </a>
      )}
      <div className="flex justify-center gap-3">
        <Link to="/assaggi-darte" className="btn-outline" data-testid="payment-back-event">Assaggi d'Arte</Link>
        <Link to="/" className="btn-primary" data-testid="payment-back-home">Torna alla Home</Link>
      </div>
    </div>
  );
}
